import React, { useState } from "react";
import { Loader2, RefreshCw, Play } from "lucide-react";
import api from "../../../lib/api";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { toast } from "sonner";

// The model each platform is asked with, taken from the account's own list
// rather than pinned in code — a pinned snapshot gets retired, and two accounts
// are not offered the same set. One test question proves the connection before
// a full run spends anything.
export default function PlatformModels({ settings, onSaved }) {
  const [lists, setLists] = useState({});
  const [loading, setLoading] = useState("");
  const [testing, setTesting] = useState("");
  const [tests, setTests] = useState({});

  const platforms = (settings.platform_catalogue || []).filter((p) => p.automatable);

  const loadModels = async (provider) => {
    setLoading(provider);
    try {
      const r = await api.get("/admin/ai-monitor/provider-models", { params: { provider } });
      setLists((l) => ({ ...l, [provider]: r.data.models }));
      toast.success(`${r.data.models.length} models available on that account.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read the model list.");
    } finally {
      setLoading("");
    }
  };

  const setModel = async (platform, model) => {
    try {
      await api.post("/admin/ai-monitor/settings", { models: { ...settings.models, [platform]: model } });
      toast.success(`${platform} will be asked with ${model}.`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the model.");
    }
  };

  const runTest = async (platform) => {
    setTesting(platform);
    setTests((t) => ({ ...t, [platform]: null }));
    try {
      const r = await api.post("/admin/ai-monitor/test-question", { platform });
      setTests((t) => ({ ...t, [platform]: r.data }));
      if (r.data.ok) toast.success(`${platform} answered in ${r.data.seconds}s.`);
      else toast.error(r.data.error);
    } catch (e) {
      const why = e?.response?.data?.detail || "The test could not be run.";
      setTests((t) => ({ ...t, [platform]: { ok: false, error: why } }));
      toast.error(why);
    } finally {
      setTesting("");
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="ai-platform-models">
      <h4 className="font-bold text-navy text-sm">Model and connection test</h4>
      <p className="text-xs text-slate-500 mt-1">
        One test question costs a few pence and is kept out of the monitoring figures — a connection test is
        not a measurement.
      </p>
      <div className="mt-3 space-y-4">
        {platforms.map((p) => {
          const list = lists[p.provider] || [];
          const test = tests[p.name];
          return (
            <div key={p.name} className="border-t border-slate-100 pt-3 first:border-0 first:pt-0"
                 data-testid={`ai-model-row-${p.name.toLowerCase()}`}>
              <div className="flex flex-wrap items-end gap-2">
                <label className="flex-1 min-w-[260px]">
                  <span className="text-xs font-semibold text-slate-500">{p.name} model</span>
                  {list.length > 0 ? (
                    <select value={settings.models?.[p.name] || ""} className="mt-1 w-full text-sm border border-slate-200 rounded-md px-2 py-2"
                            onChange={(e) => setModel(p.name, e.target.value)}
                            data-testid={`ai-model-select-${p.name.toLowerCase()}`}>
                      {list.map((m) => <option key={m.id} value={m.id}>{m.display_name} ({m.id})</option>)}
                    </select>
                  ) : (
                    <Input defaultValue={settings.models?.[p.name] || ""} data-testid={`ai-model-input-${p.name.toLowerCase()}`}
                           onBlur={(e) => e.target.value !== settings.models?.[p.name] && setModel(p.name, e.target.value)} />
                  )}
                </label>
                {p.needs_own_key && p.ready && (
                  <Button size="sm" variant="outline" onClick={() => loadModels(p.provider)} disabled={loading === p.provider}
                          data-testid={`ai-model-list-${p.name.toLowerCase()}`}>
                    {loading === p.provider ? <Loader2 className="animate-spin mr-2" size={14} />
                      : <RefreshCw className="mr-2" size={14} />}
                    List my models
                  </Button>
                )}
                <Button size="sm" onClick={() => runTest(p.name)} disabled={!p.ready || testing === p.name}
                        data-testid={`ai-model-test-${p.name.toLowerCase()}`}>
                  {testing === p.name ? <Loader2 className="animate-spin mr-2" size={14} /> : <Play className="mr-2" size={14} />}
                  Test with one question
                </Button>
              </div>
              {!p.ready && <p className="text-[11px] text-slate-400 mt-1">{p.blocked_reason}</p>}
              {test && (
                <div className={`mt-2 text-xs rounded-md p-3 ${test.ok ? "bg-emerald-50 text-emerald-900" : "bg-red-50 text-red-700"}`}
                     data-testid={`ai-model-test-result-${p.name.toLowerCase()}`}>
                  {test.ok ? (
                    <>
                      <p className="font-semibold">
                        {test.model} answered in {test.seconds}s · {test.citations.length} sources ·
                        about £{test.cost_estimate_gbp.toFixed(2)}
                      </p>
                      <p className="mt-1">{test.web_search_note} {test.grounding && `(${test.grounding})`}</p>
                      <p className="mt-2 text-slate-600 whitespace-pre-wrap">{test.excerpt}</p>
                      {test.citations.length > 0 && (
                        <ul className="mt-2 space-y-0.5">
                          {test.citations.slice(0, 8).map((c, i) => (
                            <li key={i} className="truncate">
                              <a href={c.url} target="_blank" rel="noreferrer" className="underline">
                                {c.title || c.url}
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : test.error}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
